// Shared client-side image gate for every attach entry point (picker, paste,
// drag-and-drop). Mirrors the backend's own limits (backend/src/routes/tenantUpload.js)
// so users get instant feedback instead of a round-trip 4xx.
export const IMAGE_MIME = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
export const MAX_IMAGE_BYTES = 5 * 1024 * 1024; // matches backend tenantUpload.js

// HEIC/HEIF (iPhone photos). Browsers can't RENDER these and the backend only
// stores JPEG/PNG/GIF/WEBP, so a HEIC file is converted to JPEG client-side
// (see convertHeic) BEFORE it enters the staging pipeline. We only need to
// RECOGNIZE it here. Note: many browsers report a HEIC file with an empty
// file.type, so recognition also falls back to the filename extension.
export const HEIC_MIME = ['image/heic', 'image/heif', 'image/heic-sequence', 'image/heif-sequence'];
const HEIC_EXT_RE = /\.(heic|heif)$/i;

export function isHeicFile(file) {
  if (!file) return false;
  if (file.type && HEIC_MIME.includes(file.type.toLowerCase())) return true;
  // Empty/ambiguous type: fall back to the name. (Only picker/drop files have a
  // name; pasted HEIC blobs are rare and will carry a type.)
  if (file.name && HEIC_EXT_RE.test(file.name)) return true;
  return false;
}

// The `accept` attribute for the native file picker — offer HEIC too so iPhone
// users can pick it. The size/type check still happens in validateImageFile.
export const IMAGE_ACCEPT = [...IMAGE_MIME, ...HEIC_MIME, '.heic', '.heif'].join(',');

// Validate a file that is ALREADY in a browser-renderable form (post-conversion
// for HEIC). Rejects HEIC here on purpose: callers must run it through
// convertHeic first (validateImageFile is the last gate before staging).
export function validateImageFile(file) {
  if (!file) return { ok: false };
  if (!IMAGE_MIME.includes(file.type)) return { ok: false }; // ignore silently
  if (file.size > MAX_IMAGE_BYTES) return { ok: false, error: 'Image too large. Max 5 MB.' };
  return { ok: true };
}

// Convert a HEIC/HEIF File to a JPEG File, lazy-loading the ~1-2 MB libheif wasm
// decoder only on first use (so non-HEIC users never pay for it). Returns a new
// File; throws on decode failure so callers can surface a clean error.
export async function convertHeic(file) {
  const { default: heic2any } = await import('heic2any');
  const blob = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.9 });
  // heic2any returns a Blob (single image) or Blob[] (image sequence); take the
  // first frame for sequences.
  const out = Array.isArray(blob) ? blob[0] : blob;
  const baseName = (file.name || 'image').replace(HEIC_EXT_RE, '');
  return new File([out], `${baseName}.jpg`, { type: 'image/jpeg' });
}

// One-stop helper for every attach entry point: converts HEIC to JPEG if needed,
// then validates. Returns { file, ...validateImageFile(result) }. Callers that
// need to show a "converting…" state can await this directly.
export async function prepareImageFile(file) {
  if (!file) return { ok: false };
  let prepared = file;
  if (isHeicFile(file)) {
    try {
      prepared = await convertHeic(file);
    } catch (err) {
      return { ok: false, error: "Couldn't read that HEIC image." };
    }
  }
  return { ...validateImageFile(prepared), file: prepared };
}
